import { ReactElement, useMemo } from "react";
import { CharacterData } from "../../../../utils/game/character/CharacterData";
import { WieldMode } from "../../../../utils/game/mechanics/WieldMode";

import AttributePanel from "../AttributePanel/AttributePanel";
import AttributeIconPanel from "../AttributeIconPanel/AttributeIconPanel";
import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import StatBoard from "../../../../utils/game/mechanics/StatBoard";

import accIcon from "../../../../assets/images/character_sheet/svg/attributes/acc.svg";
import maccIcon from "../../../../assets/images/character_sheet/svg/attributes/macc.svg";
import rngIcon from "../../../../assets/images/character_sheet/svg/attributes/rng.svg";
import mrngIcon from "../../../../assets/images/character_sheet/svg/attributes/mrng.svg";
import powIcon from "../../../../assets/images/character_sheet/svg/attributes/pow.svg";
import mpowIcon from "../../../../assets/images/character_sheet/svg/attributes/mpow.svg";
import dmgIcon from "../../../../assets/images/character_sheet/svg/attributes/dmg.svg";
import mdmgIcon from "../../../../assets/images/character_sheet/svg/attributes/mdmg.svg";

type Props = Readonly<{
    characterData: CharacterData,
    statBoard: StatBoard,
    dmgDisplayMode: "physical" | "magic"
    useIconAttributes?: boolean,
    wieldMode?: WieldMode,
    onAttributeTitleClick?: (attributeName: string) => void,
}>;

export default function WeaponValuesPanel({
    characterData, 
    statBoard, 
    dmgDisplayMode, 
    useIconAttributes = true, 
    wieldMode = WieldMode.RIGHT_HAND_WIELD,
    onAttributeTitleClick
}: Props): ReactElement {
    const visibleHands = useMemo(() => {
        switch(wieldMode) {
            case WieldMode.DUAL_WIELD:
                return ["lh", "rh"];
            case WieldMode.LEFT_HAND_WIELD:
                return ["lh"];
            case WieldMode.RIGHT_HAND_WIELD:
                return ["rh"];
            case WieldMode.TWO_HAND_WIELD:
                return characterData.lh != null ? ["lh"] : characterData.rh != null ? ["rh"] : [];
        }
    }, [characterData.lh, characterData.rh, wieldMode]);

    return <FlexColumn gap={8}>
        {visibleHands.map(hand => (
            <FlexColumn gap={4} key={hand}>
                <FlexRow>
                    {useIconAttributes ? 
                        <AttributeIconPanel 
                            icon={dmgDisplayMode === "physical" ? accIcon : maccIcon}
                            statBoard={statBoard} 
                            statKey={dmgDisplayMode === "physical" ? "ACC" : "MACC"}
                            qualifiers={[hand]}
                            onIconClick={() => {
                                onAttributeTitleClick?.("Accuracy");
                            }} 
                        /> : 
                        <AttributePanel
                            statBoard={statBoard}
                            statKey={dmgDisplayMode === "physical" ? "ACC" : "MACC"}
                            qualifiers={[hand]}
                            onAttributeTitleClick={() => {
                                onAttributeTitleClick?.("Accuracy");
                            }}
                        />
                    }

                    {useIconAttributes ? 
                        <AttributeIconPanel 
                            icon={dmgDisplayMode === "physical" ? rngIcon : mrngIcon}
                            statBoard={statBoard} 
                            statKey={dmgDisplayMode === "physical" ? "RNG" : "MRNG"}
                            qualifiers={[hand]}
                            onIconClick={() => {
                                onAttributeTitleClick?.("Combat Range");
                            }} 
                        /> : 
                        <AttributePanel
                            statBoard={statBoard}
                            statKey={dmgDisplayMode === "physical" ? "RNG" : "MRNG"}
                            qualifiers={[hand]}
                            onAttributeTitleClick={() => {
                                onAttributeTitleClick?.("Combat Range");
                            }}
                        />
                    }
                </FlexRow>

                <FlexRow>
                    {useIconAttributes ? 
                        <AttributeIconPanel 
                            icon={dmgDisplayMode === "physical" ? powIcon : mpowIcon}
                            statBoard={statBoard} 
                            statKey={dmgDisplayMode === "physical" ? "POW" : "MPOW"}
                            qualifiers={[hand]}
                            onIconClick={() => {
                                onAttributeTitleClick?.("Power");
                            }} 
                        /> : 
                        <AttributePanel
                            statBoard={statBoard}
                            statKey={dmgDisplayMode === "physical" ? "POW" : "MPOW"}
                            qualifiers={[hand]}
                            onAttributeTitleClick={() => {
                                onAttributeTitleClick?.("Power");
                            }}
                        />
                    }
                    {useIconAttributes ? 
                        <AttributeIconPanel 
                            icon={dmgDisplayMode === "physical" ? dmgIcon : mdmgIcon}
                            statBoard={statBoard} 
                            statKey={dmgDisplayMode === "physical" ? "DMG" : "MDMG"}
                            qualifiers={[hand]}
                            onIconClick={() => {
                                onAttributeTitleClick?.("Damage");
                            }} 
                        /> : 
                        <AttributePanel
                            statBoard={statBoard}
                            statKey={dmgDisplayMode === "physical" ? "DMG" : "MDMG"}
                            qualifiers={[hand]}
                            onAttributeTitleClick={() => {
                                onAttributeTitleClick?.("Damage");
                            }}
                        />
                    }
                </FlexRow>
            </FlexColumn>
        ))}
    </FlexColumn>;
}